/**
 * Admin Location Assignment Dialog Component
 * 
 * Dialog for creating/editing admin location assignments.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, MapPin, AlertCircle, CheckCircle2, GripVertical, Users, Building2, Info } from "lucide-react";
import { userService, type Member } from "@/services/userService";
import { adminService, type AdminLocationAssignment, type AdminLevel, type AdminPrivilege, type AdminLevelItem } from "@/services/adminService";
import { locationService } from "@/services/locationService";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

interface AdminLocationAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assignment?: AdminLocationAssignment | null;
  onSuccess?: () => void;
}

export function AdminLocationAssignmentDialog({
  open,
  onOpenChange,
  assignment,
  onSuccess,
}: AdminLocationAssignmentDialogProps) {
  const { user: authUser, refreshUser } = useAuth();
  const [callsign, setCallsign] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState<Member | null>(null);
  const [selectedAdminLevel, setSelectedAdminLevel] = React.useState<string>("");
  const [adminLevels, setAdminLevels] = React.useState<AdminLevelItem[]>([]);
  const [loadingAdminLevels, setLoadingAdminLevels] = React.useState(false);
  const [level, setLevel] = React.useState<AdminLevel>("nasional");
  const [selectedProvinces, setSelectedProvinces] = React.useState<string[]>([]);
  const [selectedCities, setSelectedCities] = React.useState<string[]>([]);
  const [selectedPrivileges, setSelectedPrivileges] = React.useState<AdminPrivilege[]>([]);
  const [isActive, setIsActive] = React.useState(true);
  const [notes, setNotes] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Location data
  const [provinces, setProvinces] = React.useState<string[]>([]);
  const [citiesByProvince, setCitiesByProvince] = React.useState<Map<string, string[]>>(new Map());
  const [loadingProvinces, setLoadingProvinces] = React.useState(false);
  const [loadingCities, setLoadingCities] = React.useState(false);

  // Determine user's max admin level from assignments
  const maxLevel = React.useMemo(() => {
    const numericLevel = Number(authUser?.userLevel);
    if (Number.isFinite(numericLevel) && numericLevel === 3) return "nasional" as const;
    const levels = authUser?.adminAssignments?.filter((a) => a.isActive).map((a) => a.level) ?? [];
    if (levels.includes("nasional")) return "nasional" as const;
    if (levels.includes("province")) return "province" as const;
    if (levels.includes("city")) return "city" as const;
    return null;
  }, [authUser]);

  // Get allowed provinces from user's admin assignments (for non-superadmin)
  const allowedProvinces = React.useMemo(() => {
    const numericLevel = Number(authUser?.userLevel);
    if (Number.isFinite(numericLevel) && numericLevel === 3) return null; // superadmin - no restriction
    if (maxLevel === "nasional") return null; // nasional admin - no restriction
    const provinces = new Set<string>();
    authUser?.adminAssignments
      ?.filter((a) => a.isActive && a.provinces)
      .forEach((a) => a.provinces?.forEach((p) => provinces.add(p)));
    console.log("[AdminLocationAssignmentDialog] allowedProvinces:", {
      maxLevel,
      adminAssignments: authUser?.adminAssignments,
      provinces: Array.from(provinces),
    });
    return provinces.size > 0 ? Array.from(provinces) : null;
  }, [authUser, maxLevel]);

  // Dragging state
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState<{ x: number; y: number } | null>(null);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);

  // Load admin levels on mount
  React.useEffect(() => {
    if (open && !assignment) {
      const loadAdminLevels = async () => {
        setLoadingAdminLevels(true);
        try {
          const data = await adminService.fetchAdminLevels();
          setAdminLevels(data);
        } catch (err) {
          console.error("Error loading admin levels:", err);
        } finally {
          setLoadingAdminLevels(false);
        }
      };
      loadAdminLevels();
    }
  }, [open, assignment]);

  // Load provinces on mount
  React.useEffect(() => {
    if (open) {
      const loadProvinces = async () => {
        setLoadingProvinces(true);
        try {
          const data = await locationService.fetchProvinces();
          setProvinces(data);
        } catch (err) {
          console.error("Error loading provinces:", err);
        } finally {
          setLoadingProvinces(false);
        }
      };
      loadProvinces();
    }
  }, [open]);

  // Handle admin level template selection
  React.useEffect(() => {
    if (selectedAdminLevel && !assignment) {
      const adminLevel = adminLevels.find((al) => al._id === selectedAdminLevel);
      if (adminLevel) {
        setLevel(adminLevel.level);
        setSelectedPrivileges(adminLevel.defaultPrivileges || []);
        // Clear location selections when template is selected
        if (adminLevel.level === "nasional") {
          setSelectedProvinces([]);
          setSelectedCities([]);
        } else if (adminLevel.level === "province") {
          setSelectedCities([]);
        }
      }
    }
  }, [selectedAdminLevel, adminLevels, assignment]);

  // Load cities for selected provinces
  React.useEffect(() => {
    if (open && level === "city" && selectedProvinces.length > 0) {
      const loadCities = async () => {
        setLoadingCities(true);
        const citiesMap = new Map<string, string[]>();
        
        try {
          await Promise.all(
            selectedProvinces.map(async (province) => {
              const cities = await locationService.fetchCitiesByProvince(province);
              citiesMap.set(province, cities);
            })
          );
          setCitiesByProvince(citiesMap);
          
          // After cities are loaded, auto-select user's city if it exists (only when creating new assignment)
          if (!assignment && selectedUser?.kabupaten) {
            // Check if the user's city is in any of the loaded provinces
            const allLoadedCities: string[] = [];
            citiesMap.forEach((cities) => {
              allLoadedCities.push(...cities);
            });
            
            // Try exact match first, then case-insensitive match
            const userCity = selectedUser.kabupaten.trim();
            const foundCity = allLoadedCities.find(city => 
              city.trim() === userCity || 
              city.trim().toLowerCase() === userCity.toLowerCase()
            );
            
            if (foundCity) {
              setSelectedCities((prev) => {
                if (!prev.includes(foundCity)) {
                  return [...prev, foundCity];
                }
                return prev;
              });
              console.log("[AdminLocationAssignmentDialog] Auto-selected city:", foundCity);
            } else {
              console.warn("[AdminLocationAssignmentDialog] User's city not found in loaded cities:", {
                userCity,
                availableCities: allLoadedCities.slice(0, 5), // Log first 5 for debugging
              });
            }
          }
        } catch (err) {
          console.error("Error loading cities:", err);
        } finally {
          setLoadingCities(false);
        }
      };
      loadCities();
    } else {
      setCitiesByProvince(new Map());
    }
  }, [open, level, selectedProvinces, selectedUser, assignment]);

  // Initialize form from assignment
  React.useEffect(() => {
    if (assignment && open) {
      if (assignment.admin) {
        setSelectedUser({
          _id: assignment.admin._id,
          callsign: assignment.admin.callsign,
          nama_lengkap: assignment.admin.nama_lengkap,
          email: assignment.admin.email,
          provinsi: assignment.admin.provinsi,
          kabupaten: assignment.admin.kabupaten,
        } as Member);
        setCallsign(assignment.admin.callsign);
      } else {
        setSelectedUser(null);
        setCallsign("");
      }
      setLevel(assignment.level);
      setSelectedProvinces(assignment.provinces || []);
      setSelectedCities(assignment.cities || []);
      setSelectedPrivileges(assignment.privileges || []);
      setIsActive(assignment.isActive);
      setNotes(assignment.notes || "");
    } else if (open && !assignment) {
      // Reset form
      setCallsign("");
      setSelectedUser(null);
      setSelectedAdminLevel("");
      setLevel("nasional");
      setSelectedProvinces([]);
      setSelectedCities([]);
      setSelectedPrivileges([]);
      setIsActive(true);
      setNotes("");
      setError(null);
      setPosition(null);
    }
  }, [assignment, open]);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!headerRef.current || !dialogRef.current) return;
    
    e.preventDefault();
    
    const rect = dialogRef.current.getBoundingClientRect();
    const currentPosition = position || {
      x: rect.left,
      y: rect.top,
    };
    
    setDragOffset({
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y,
    });
    
    if (!position) {
      setPosition({
        x: rect.left,
        y: rect.top,
      });
    }
    
    setIsDragging(true);
  };

  // Handle drag
  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dialogRef.current) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - dialogRef.current.offsetWidth;
      const maxY = window.innerHeight - dialogRef.current.offsetHeight;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, position]);

  // Search user by callsign
  const handleSearchUser = async () => {
    if (!callsign.trim()) {
      setError("Callsign harus diisi");
      return;
    }

    setSearching(true);
    setError(null);
    setSelectedUser(null);

    try {
      const result = await userService.fetchMembers({
        search: callsign.trim().toUpperCase(),
        limit: 1,
      });

      if (result.data.length > 0) {
        const user = result.data[0];
        if (user) {
          setSelectedUser(user);
          
          // Auto-select user's province and city as default (only when creating new assignment)
          if (!assignment && user.provinsi) {
            setSelectedProvinces((prev) => {
              if (!prev.includes(user.provinsi!)) {
                return [...prev, user.provinsi!];
              }
              return prev;
            });
          }
          
          // Note: City auto-selection will be handled by the useEffect that loads cities
          // We don't set it here because cities need to be loaded first
          
          toast.success("User ditemukan", {
            description: `${user.nama_lengkap} (${user.callsign})`,
          });
        } else {
          setError("User tidak ditemukan");
          setSelectedUser(null);
        }
      } else {
        setError("User tidak ditemukan");
        setSelectedUser(null);
      }
    } catch (err) {
      console.error("Error searching user:", err);
      setError(err instanceof Error ? err.message : "Gagal mencari user");
      setSelectedUser(null);
    } finally {
      setSearching(false);
    }
  };

  // Handle province toggle
  const handleProvinceToggle = (province: string, checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedProvinces([...selectedProvinces, province]);
    } else {
      setSelectedProvinces(selectedProvinces.filter(p => p !== province));
      // Remove cities from removed provinces
      if (level === "city") {
        const citiesToRemove = citiesByProvince.get(province) || [];
        setSelectedCities(selectedCities.filter(c => !citiesToRemove.includes(c)));
      }
    }
  };

  // Handle city toggle
  const handleCityToggle = (city: string, checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedCities([...selectedCities, city]);
    } else {
      setSelectedCities(selectedCities.filter(c => c !== city));
    }
  };

  // Handle privilege toggle
  const handlePrivilegeToggle = (privilege: AdminPrivilege, checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedPrivileges([...selectedPrivileges, privilege]);
    } else {
      setSelectedPrivileges(selectedPrivileges.filter(p => p !== privilege));
    }
  };

  // Privilege options with labels
  const privilegeOptions: { value: AdminPrivilege; label: string; description: string }[] = [
    { value: "manage_users", label: "Kelola User", description: "Mengelola data anggota" },
    {
      value: "manage_user_reset_password",
      label: "Reset password anggota",
      description: "Sub-hak: reset password dari daftar anggota (admin)",
    },
    {
      value: "manage_user_set_email",
      label: "Set email anggota",
      description: "Sub-hak: ubah email anggota dari daftar anggota (admin)",
    },
    { value: "manage_locations", label: "Kelola Lokasi", description: "Mengelola data provinsi dan kota" },
    { value: "manage_payments", label: "Kelola Pembayaran", description: "Mengelola transaksi pembayaran" },
    { value: "manage_blocks", label: "Kelola Blokir", description: "Mengelola penangguhan anggota" },
    { value: "manage_silentkey", label: "Silent Key", description: "Mengelola status Silent Key anggota" },
    { value: "manage_jabatan", label: "Kelola Jabatan", description: "Mengelola jabatan dan penugasan" },
    { value: "manage_admin_assignments", label: "Kelola Penugasan Admin", description: "Mengelola penugasan admin" },
    { value: "view_reports", label: "Lihat Laporan", description: "Melihat laporan dan statistik" },
    { value: "manage_transactions", label: "Kelola Transaksi", description: "Mengelola riwayat transaksi" },
    {
      value: "manage_transactions_plan",
      label: "Kelola Rencana Transaksi",
      description: "Akses riwayat transaksi (rencana)",
    },
    {
      value: "create_transaction_plan",
      label: "Buat Rencana Transfer",
      description: "Unggah dan konfirmasi rencana transfer",
    },
    {
      value: "manage_historical_transfer",
      label: "Riwayat Transfer",
      description: "Melihat riwayat transfer",
    },
    {
      value: "manage_transfer_plans",
      label: "Kelola Daftar Rencana",
      description: "Daftar, detail, dan hapus rencana transfer",
    },
    {
      value: "create_transaction_bank_account",
      label: "Tambah Rekening Bank",
      description: "Menambah rekening bank untuk transaksi keuangan",
    },
  ];

  // Get available cities for selected provinces
  const availableCities = React.useMemo(() => {
    if (level !== "city" || selectedProvinces.length === 0) {
      return [];
    }
    const allCities: string[] = [];
    selectedProvinces.forEach(province => {
      const cities = citiesByProvince.get(province) || [];
      allCities.push(...cities);
    });
    // Remove duplicates
    return Array.from(new Set(allCities));
  }, [level, selectedProvinces, citiesByProvince]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!selectedUser) {
        setError("User harus dipilih");
        setLoading(false);
        return;
      }

      // Validate based on level
      if (level === "province" && selectedProvinces.length === 0) {
        setError("Province level admin harus memiliki setidaknya satu provinsi");
        setLoading(false);
        return;
      }

      if (level === "city") {
        if (selectedProvinces.length === 0) {
          setError("City level admin harus memiliki setidaknya satu provinsi");
          setLoading(false);
          return;
        }
        if (selectedCities.length === 0) {
          setError("City level admin harus memiliki setidaknya satu kota");
          setLoading(false);
          return;
        }
      }

      const payload = {
        admin: selectedUser._id,
        level,
        provinces: level === "nasional" ? [] : selectedProvinces,
        cities: level === "city" ? selectedCities : [],
        privileges: selectedPrivileges,
        isActive,
        notes: notes || undefined,
      };

      if (assignment) {
        await adminService.updateLocationAssignment(assignment._id, payload);
        toast.success("Penugasan lokasi berhasil diupdate", {
          description: `Penugasan lokasi untuk ${selectedUser.nama_lengkap} telah diperbarui`,
        });
      } else {
        await adminService.createLocationAssignment(payload);
        toast.success("Penugasan lokasi berhasil dibuat", {
          description: `Penugasan lokasi untuk ${selectedUser.nama_lengkap} telah dibuat`,
        });
      }

      if (authUser?.id && selectedUser._id === authUser.id) {
        await refreshUser();
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal menyimpan penugasan lokasi");
    } finally {
      setLoading(false);
    }
  };

  const getLevelConfig = (level: AdminLevel) => {
    switch (level) {
      case "nasional":
        return {
          label: "Nasional",
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/20",
          borderColor: "border-blue-200 dark:border-blue-800",
          icon: Users,
        };
      case "province":
        return {
          label: "Provinsi",
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-950/20",
          borderColor: "border-green-200 dark:border-green-800",
          icon: Building2,
        };
      case "city":
        return {
          label: "Kota/Kabupaten",
          color: "text-purple-600 dark:text-purple-400",
          bgColor: "bg-purple-50 dark:bg-purple-950/20",
          borderColor: "border-purple-200 dark:border-purple-800",
          icon: MapPin,
        };
    }
  };

  const levelConfig = getLevelConfig(level);
  const LevelIcon = levelConfig.icon;
  const isFormValid = selectedUser && (
    level === "nasional" ||
    (level === "province" && selectedProvinces.length > 0) ||
    (level === "city" && selectedProvinces.length > 0 && selectedCities.length > 0)
  );

  // Debug: Log form validation state
  React.useEffect(() => {
    if (open) {
      console.log("[AdminLocationAssignmentDialog] Form validation:", {
        selectedUser: !!selectedUser,
        level,
        selectedProvinces: selectedProvinces.length,
        selectedCities: selectedCities.length,
        isFormValid,
        loading,
      });
    }
  }, [open, selectedUser, level, selectedProvinces.length, selectedCities.length, isFormValid, loading]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogRef}
        className={cn(
          "sm:max-w-[700px] max-h-[90vh] overflow-y-auto",
          isDragging && "cursor-grabbing",
          position && "translate-x-0 translate-y-0"
        )}
        style={
          position
            ? {
                left: `${position.x}px`,
                top: `${position.y}px`,
                transform: "none",
              }
            : undefined
        }
      >
        <DialogHeader className="space-y-3 pb-4 border-b">
          <div
            ref={headerRef}
            className={cn(
              "flex items-center gap-3 cursor-grab select-none",
              isDragging && "cursor-grabbing"
            )}
            onMouseDown={handleMouseDown}
          >
            <div className={cn(
              "p-2 rounded-lg border",
              levelConfig.bgColor,
              levelConfig.borderColor
            )}>
              <LevelIcon className={cn("h-5 w-5", levelConfig.color)} />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                {assignment ? "Edit" : "Tambah"} Penugasan Lokasi Admin
                <GripVertical className="h-4 w-4 text-muted-foreground opacity-50" />
              </DialogTitle>
              <DialogDescription className="text-sm mt-1">
                {assignment ? "Update" : "Tentukan"} lokasi yang dapat dikelola oleh admin
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-5">
            {/* User Search */}
            <div className="space-y-2">
              <Label htmlFor="callsign" className="text-sm font-semibold flex items-center gap-2">
                Cari Admin
                <span className="text-red-500">*</span>
                {selectedUser && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
              </Label>
              <div className="flex gap-2">
                <input
                  id="callsign"
                  type="text"
                  value={callsign}
                  onChange={(e) => setCallsign(e.target.value.toUpperCase())}
                  placeholder="Masukkan callsign (contoh: YB8MR)"
                  disabled={loading || searching || !!assignment}
                  className="h-11 px-3 rounded-md border border-input bg-background text-base font-mono flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchUser();
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={handleSearchUser}
                  disabled={loading || searching || !callsign.trim() || !!assignment}
                  className="h-11"
                >
                  {searching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Users className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {selectedUser && (
                <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{selectedUser.nama_lengkap}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedUser.callsign} - {selectedUser.provinsi}, {selectedUser.kabupaten}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Admin Level Template Selection (only when creating new assignment) */}
            {!assignment && (
              <div className="space-y-2">
                <Label htmlFor="admin-level-template" className="text-sm font-semibold flex items-center gap-2">
                  Template Admin Level
                  <span className="text-muted-foreground font-normal text-xs">(Opsional)</span>
                </Label>
                {loadingAdminLevels ? (
                  <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Memuat admin levels...</span>
                  </div>
                ) : (
                  <Select
                    value={selectedAdminLevel || "none"}
                    onValueChange={(value) => {
                      setSelectedAdminLevel(value === "none" ? "" : value);
                    }}
                    disabled={loading || loadingAdminLevels}
                  >
                    <SelectTrigger id="admin-level-template" className="h-11 text-base">
                      <SelectValue placeholder="Pilih template admin level (opsional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Tidak menggunakan template</SelectItem>
                      {adminLevels.map((al) => (
                        <SelectItem key={al._id} value={al._id}>
                          {al.name} ({al.level})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Pilih template untuk mengisi level dan default privileges secara otomatis
                </p>
              </div>
            )}

            {/* Level Selection */}
            <div className="space-y-2">
              <Label htmlFor="level" className="text-sm font-semibold flex items-center gap-2">
                Level Admin
                <span className="text-red-500">*</span>
                {level && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
              </Label>
              <Select
                value={level}
                onValueChange={(value) => {
                  setLevel(value as AdminLevel);
                  // Clear template selection when level is manually changed
                  if (selectedAdminLevel) {
                    setSelectedAdminLevel("");
                  }
                  // Clear selections when level changes
                  if (value === "nasional") {
                    setSelectedProvinces([]);
                    setSelectedCities([]);
                  } else if (value === "province") {
                    setSelectedCities([]);
                  }
                }}
                disabled={loading || !!assignment}
              >
                <SelectTrigger id="level" className="h-11 text-base">
                  <SelectValue placeholder="Pilih level" />
                </SelectTrigger>
                <SelectContent>
                  {maxLevel === "nasional" && <SelectItem value="nasional">Nasional</SelectItem>}
                  {(maxLevel === "nasional" || maxLevel === "province") && <SelectItem value="province">Provinsi</SelectItem>}
                  <SelectItem value="city">Kota/Kabupaten</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Province Multi-Select - Only for province and city level */}
            {level !== "nasional" && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  Provinsi
                  <span className="text-red-500">*</span>
                  {selectedProvinces.length > 0 && (
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  )}
                </Label>
                {loadingProvinces ? (
                  <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Memuat provinsi...</span>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 max-h-[200px] overflow-y-auto space-y-2">
                    {(allowedProvinces ?? provinces).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Tidak ada provinsi tersedia
                      </p>
                    ) : (
                      (allowedProvinces ?? provinces).map((province) => (
                        <div key={province} className="flex items-center space-x-2">
                          <Checkbox
                            id={`province-${province}`}
                            checked={selectedProvinces.includes(province)}
                            onCheckedChange={(checked: boolean | "indeterminate") =>
                              handleProvinceToggle(province, checked)
                            }
                            disabled={loading}
                          />
                          <label
                            htmlFor={`province-${province}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                          >
                            {province}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {selectedProvinces.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {selectedProvinces.length} provinsi dipilih
                  </p>
                )}
              </div>
            )}

            {/* City Multi-Select - Only for city level */}
            {level === "city" && selectedProvinces.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  Kota/Kabupaten
                  <span className="text-red-500">*</span>
                  {selectedCities.length > 0 && (
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  )}
                </Label>
                {loadingCities ? (
                  <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Memuat kota...</span>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 max-h-[200px] overflow-y-auto space-y-2">
                    {availableCities.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        Tidak ada kota tersedia untuk provinsi yang dipilih
                      </p>
                    ) : (
                      availableCities.map((city) => (
                        <div key={city} className="flex items-center space-x-2">
                          <Checkbox
                            id={`city-${city}`}
                            checked={selectedCities.includes(city)}
                            onCheckedChange={(checked: boolean | "indeterminate") =>
                              handleCityToggle(city, checked)
                            }
                            disabled={loading}
                          />
                          <label
                            htmlFor={`city-${city}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                          >
                            {city}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {selectedCities.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {selectedCities.length} kota dipilih
                  </p>
                )}
              </div>
            )}

            {/* Privileges */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                Hak Akses (Privileges)
                <span className="text-muted-foreground font-normal text-xs">
                  ({selectedPrivileges.length} dipilih)
                </span>
              </Label>
              <div className="border rounded-lg p-4 max-h-[300px] overflow-y-auto space-y-3">
                {privilegeOptions.map((option) => (
                  <div key={option.value} className="flex items-start space-x-3">
                    <Checkbox
                      id={`privilege-${option.value}`}
                      checked={selectedPrivileges.includes(option.value)}
                      onCheckedChange={(checked: boolean | "indeterminate") =>
                        handlePrivilegeToggle(option.value, checked)
                      }
                      disabled={loading}
                      className="mt-1"
                    />
                    <label
                      htmlFor={`privilege-${option.value}`}
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                    >
                      <div className="font-medium">{option.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {option.description}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="isActive" className="text-sm font-semibold">Status</Label>
              <Select
                value={isActive ? "active" : "inactive"}
                onValueChange={(value) => setIsActive(value === "active")}
                disabled={loading}
              >
                <SelectTrigger id="isActive" className="h-11 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="inactive">Tidak Aktif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-semibold">
                Catatan
                <span className="text-muted-foreground font-normal ml-2">(Opsional)</span>
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Catatan tambahan..."
                rows={3}
                disabled={loading}
                className="resize-none text-base"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-11"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={loading || !isFormValid}
              className="h-11 min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <MapPin className="mr-2 h-4 w-4" />
                  {assignment ? "Update" : "Simpan"}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

